import { type ElementType, type PropsWithChildren, forwardRef } from "react";
import classes from "./DDSLabelText.module.css";

const Variants = [
  "primary",
  "secondary",
  "tertiary",
  "light",
  "disabled",
  "accent",
  "success",
  "warning",
  "danger",
  "plus",
] as const;

export const VariantClasses = Object.fromEntries(Variants.map((v) => [v, classes[v]]));

export const LabelLevelClasses = {
  labelLg: classes.labelLg,
  labelMd: classes.labelMd,
  labelSm: classes.labelSm,
} as const;

export type DDSLabelTextProps = {
  // If you're looking for h1 or h2, you will want a DDSHeadlineText.
  as?: "span" | "h2" | "h3" | "h4" | "h5" | "label";
  /**
   * Label size — matches the `--label-{size}-*` CSS tokens.
   *
   * Renamed in 2026 from numeric levels to t-shirt strings: `1`→`"lg"`, `2`→`"md"`,
   * `3`→`"sm"`. See the [type system migration spec](https://linear.app/classdojo/document/type-system-migration-spec-and-codefigma-differences-a635b955ad0a)
   * for the full mapping (Figma may still display legacy "Label/1" style names on stale comps).
   */
  level?: "lg" | "md" | "sm";
  className?: string;
  variant?: (typeof Variants)[number];
  "data-name"?: string;
  role?: string;
  id?: string;
  htmlFor?: string;
};

const LEVEL_TO_CLASS = {
  lg: "labelLg",
  md: "labelMd",
  sm: "labelSm",
} as const;

export const DDSLabelText = forwardRef<HTMLElement, PropsWithChildren<DDSLabelTextProps>>(
  (
    {
      as: tagName = "span",
      level = "lg",
      className,
      children,
      variant = "primary",
      "data-name": dataName,
      role,
      id,
      htmlFor,
    },
    ref,
  ) => {
    const Tag = tagName as ElementType;
    const classNames = [className, classes[variant], classes[LEVEL_TO_CLASS[level]]].filter((c) => !!c);

    return (
      <Tag
        ref={ref}
        className={classNames.join(" ")}
        data-name={dataName}
        role={role}
        id={id}
        htmlFor={tagName === "label" ? htmlFor : undefined}
      >
        {children}
      </Tag>
    );
  },
);

DDSLabelText.displayName = "DDSLabelText";
